// Utility functions for working with SVG strings in the editor
// Ensure the SVG has a viewBox when width/height present
export function ensureViewBox(svgString) {
  if (!svgString || typeof svgString !== 'string') return svgString || ''
  if (/viewBox\s*=/.test(svgString)) return svgString
  const w = svgString.match(/<svg[^>]*\swidth=\"([0-9.]+)\w*\"/i)
  const h = svgString.match(/<svg[^>]*\sheight=\"([0-9.]+)\w*\"/i)
  if (w && h) {
    return svgString.replace(/<svg([^>]*)>/, `<svg$1 viewBox=\"0 0 ${parseFloat(w[1])} ${parseFloat(h[1])}\">`)
  }
  return svgString
}

// Inject an override style scoped to a unique id to avoid affecting other SVGs
export function injectOverrideStyleScoped(svgString, scopeId, { fill, stroke, strokeWidth } = {}, { important = true } = {}) {
  if (!svgString || typeof svgString !== 'string') return svgString || ''
  const imp = important ? ' !important' : ''
  const f = typeof fill === 'string' ? fill : 'currentColor'
  const st = typeof stroke === 'string' ? stroke : 'none'
  const sw = typeof strokeWidth === 'number' ? `${strokeWidth}px` : (strokeWidth ? String(strokeWidth) : '1px')
  const id = scopeId ? String(scopeId) : `svg-${Math.random().toString(36).slice(2)}`
  // Ensure root has id
  let withId = svgString.replace(/<svg([^>]*)>/, (m, attrs) => (/\sid=\"/.test(attrs) ? `<svg${attrs}>` : `<svg${attrs} id=\"${id}\">`))
  const styleTag = `<style>#${id} path,#${id} rect,#${id} circle,#${id} ellipse,#${id} line,#${id} polyline,#${id} polygon,#${id} text{fill:${f}${imp};stroke:${st}${imp};stroke-width:${sw}${imp};vector-effect:non-scaling-stroke;}</style>`
  return withId.replace(/<svg([^>]*)>/, `<svg$1>${styleTag}`)
}

// Keep this file small and dependency-free; use regex carefully and conservatively.

// Remove presentation fill/stroke attributes and inline style rules so external styles can take effect
export function normalizeSvgColors(svgString) {
  if (!svgString || typeof svgString !== 'string') return svgString || '';
  let s = svgString;
  // Remove fill/stroke/stroke-width attributes on elements
  s = s.replace(/\sfill="[^"]*"/g, '');
  s = s.replace(/\sstroke="[^"]*"/g, '');
  s = s.replace(/\sstroke-width="[^"]*"/g, '');

  // Tidy style attributes: remove fill:, stroke:, stroke-width: rules
  s = s.replace(/style="([^"]*)"/g, (m, style) => {
    const cleaned = style
      .replace(/(^|;)\s*fill\s*:\s*[^;]+;?/gi, ';')
      .replace(/(^|;)\s*stroke\s*:\s*[^;]+;?/gi, ';')
      .replace(/(^|;)\s*stroke-width\s*:\s*[^;]+;?/gi, ';')
      .replace(/;+\s*$/g, '') // trailing semicolons
      .replace(/^;+/g, '') // leading semicolons
    ;
    return cleaned ? `style="${cleaned}"` : '';
  });

  return s;
}

// Inject a strong override <style> block into the top-level <svg> tag
export function injectOverrideStyle(svgString, { fill, stroke, strokeWidth } = {}, { important = true } = {}) {
  if (!svgString || typeof svgString !== 'string') return svgString || '';
  const imp = important ? ' !important' : '';
  const f = typeof fill === 'string' ? fill : 'currentColor';
  const st = typeof stroke === 'string' ? stroke : 'none';
  const sw = typeof strokeWidth === 'number' ? `${strokeWidth}px` : (strokeWidth ? String(strokeWidth) : '1px');

  const styleTag = `<style>path,rect,circle,ellipse,line,polyline,polygon,text{fill:${f}${imp};stroke:${st}${imp};stroke-width:${sw}${imp};vector-effect:non-scaling-stroke;}</style>`;
  return svgString.replace(/<svg([^>]*)>/, `<svg$1>${styleTag}`);
}


// ---------------------------------------------------------------------------
// Helpers for first-class `type:"svg"` items that carry an inline `svgContent`
// string (a full <svg ...>...</svg> document). Unlike the legacy `svg-graphic`
// pipeline — which force-overrides every fill/stroke to a single color — an
// imported SVG usually ships its own multi-color palette that we want to
// PRESERVE. So the default here keeps the SVG's own colors and only scales the
// root <svg> to fill the item box. Recoloring is opt-in.
// ---------------------------------------------------------------------------

/**
 * Prepare an inline SVG document string for rendering inside an item box.
 * - Ensures a viewBox (so it scales predictably).
 * - Rewrites the root <svg> to width/height 100% so it fills the box; the
 *   caller controls the box aspect. `preserveAspectRatio` defaults to
 *   'xMidYMid meet' (no distortion); pass 'none' to stretch.
 * - Optionally overrides fill/stroke/strokeWidth (opt-in recolor). When
 *   `recolor` is omitted/false the SVG's native colors are kept untouched.
 *
 * @param {string} svgContent - the raw <svg> document
 * @param {Object} opts
 * @param {string} [opts.scopeId] - unique id used to scope recolor styles
 * @param {string} [opts.preserveAspectRatio='xMidYMid meet']
 * @param {boolean} [opts.recolor=false] - when true, apply fill/stroke override
 * @param {string} [opts.fill]
 * @param {string} [opts.stroke]
 * @param {number|string} [opts.strokeWidth]
 * @returns {string} the prepared SVG string
 */
export function prepareSvgContent(svgContent, opts = {}) {
  if (!svgContent || typeof svgContent !== 'string') return ''
  const {
    scopeId,
    preserveAspectRatio = 'xMidYMid meet',
    recolor = false,
    fill,
    stroke,
    strokeWidth,
  } = opts

  let svg = ensureViewBox(svgContent)
  const id = scopeId ? String(scopeId) : `svg-${Math.random().toString(36).slice(2)}`

  // Optional recolor: strip native colors then inject a scoped override style.
  if (recolor) {
    svg = injectOverrideStyleScoped(normalizeSvgColors(svg), id, { fill, stroke, strokeWidth }, { important: true })
  }

  // Rewrite the root <svg> tag: ensure id, drop any hard width/height so our
  // 100%/100% wins, and set preserveAspectRatio + overflow.
  svg = svg.replace(/<svg([^>]*)>/, (_m, attrs) => {
    let a = attrs
    // Remove existing width/height/preserveAspectRatio to avoid conflicts.
    a = a.replace(/\swidth="[^"]*"/i, '').replace(/\sheight="[^"]*"/i, '')
    a = a.replace(/\spreserveAspectRatio="[^"]*"/i, '')
    const hasId = /\sid="/.test(a)
    const idAttr = hasId ? '' : ` id="${id}"`
    return `<svg${a}${idAttr} width="100%" height="100%" preserveAspectRatio="${preserveAspectRatio}" style="overflow: visible; display: block;">`
  })

  return svg
}

/**
 * Best-effort parse of an SVG's intrinsic width/height from its viewBox (or
 * width/height attrs), used to seed an item's box size on import. Falls back to
 * a square default. Returns { width, height }.
 */
export function getSvgIntrinsicSize(svgContent, fallback = 100) {
  if (!svgContent || typeof svgContent !== 'string') return { width: fallback, height: fallback }
  const vb = svgContent.match(/viewBox\s*=\s*"\s*[-\d.]+[ ,]+[-\d.]+[ ,]+([\d.]+)[ ,]+([\d.]+)\s*"/i)
  if (vb) {
    const w = parseFloat(vb[1])
    const h = parseFloat(vb[2])
    if (w > 0 && h > 0) return { width: w, height: h }
  }
  const wM = svgContent.match(/<svg[^>]*\swidth="([\d.]+)/i)
  const hM = svgContent.match(/<svg[^>]*\sheight="([\d.]+)/i)
  if (wM && hM) {
    const w = parseFloat(wM[1])
    const h = parseFloat(hM[1])
    if (w > 0 && h > 0) return { width: w, height: h }
  }
  return { width: fallback, height: fallback }
}

/**
 * Lightweight sanity check that a string looks like an SVG document.
 */
export function isLikelySvg(str) {
  return typeof str === 'string' && /<svg[\s>]/i.test(str) && /<\/svg>/i.test(str)
}
